import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Sparkles, CheckCircle2, AlertTriangle, AlertOctagon, Wand2, HelpCircle, ExternalLink, Lightbulb } from "lucide-react";

export const CHECK_FIELDS = [
  ["question_kjv", "Question (KJV)"], ["question_esv", "Question (ESV)"], ["who_asked", "Asked by"], ["who_asked_detail", "Asked by details"],
  ["asked_to", "Asked to"], ["asked_to_detail", "Asked to details"], ["question_types", "Question type"], ["themes", "Themes"], ["scene", "The Scene"],
  ["purpose", "Why It Was Asked"], ["response", "The Response"], ["historical_context", "Historical Context"], ["commentary", "The Bigger Picture"],
  ["application", "For Your Life Today"], ["reflection_prompt", "Reflection prompt"], ["ask_god", "Ask God"], ["ask_each_other", "Ask Yourself and Others"],
  ["related_scriptures", "Related scriptures"],
];
const STATUS = {
  sound: { Icon: CheckCircle2, cls: "text-emerald-700 border-emerald-300 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-800", label: "Sound" },
  minor: { Icon: AlertTriangle, cls: "text-amber-700 border-amber-300 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-800", label: "Minor" },
  concern: { Icon: AlertOctagon, cls: "text-red-700 border-red-300 bg-red-50 dark:text-red-300 dark:bg-red-950/40 dark:border-red-800", label: "Concern" },
  unverified: { Icon: HelpCircle, cls: "text-sky-800 border-sky-300 bg-sky-50 dark:text-sky-300 dark:bg-sky-950/40 dark:border-sky-800", label: "Unverified" },
};

/* Claude-powered biblical accuracy review of a draft's fields. `draft` is the live form; `onApply(field, text)` writes a fix back. */
export default function AccuracyCheck({ questionId, draft, onApply }) {
  const [selected, setSelected] = useState(() => new Set(CHECK_FIELDS.map(([k]) => k)));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const all = selected.size === CHECK_FIELDS.length;

  const run = async (fields) => {
    setBusy(true);
    setResult(null);
    try {
      const body = { fields, draft: Object.fromEntries(CHECK_FIELDS.map(([k]) => [k, draft?.[k]]).filter(([, v]) => v !== undefined)) };
      const { data: job } = await api.post(`/admin/questions/${questionId}/accuracy-check`, body);
      let status = job;
      while (status.status === "running") {
        await new Promise((res) => setTimeout(res, 2500));
        status = (await api.get(`/admin/accuracy/jobs/${job.job_id}`)).data;
      }
      if (status.status === "error") throw { response: { data: { detail: status.detail } } };
      setResult(status.result);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const toggle = (k) => setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  return (
    <div className="flex min-h-[32rem] flex-col rounded-2xl border border-border bg-card" data-testid="accuracy-check">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="inline-flex items-center gap-2 font-serif text-lg font-semibold text-foreground"><ShieldCheck className="h-4 w-4 text-gold" /> Accuracy check</span>
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Claude · live evidence only</span>
      </div>
      <div className="space-y-4 p-4">
        <p className="text-sm text-muted-foreground">Fetches this verse live from the trusted translations, commentaries and lexicon listed under Trusted sources &amp; rules on the Review queue page, then has Claude check the fields against <em>that evidence only</em> — never its own memory, never the site's imported texts. Anything the evidence can't settle is marked Unverified.</p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => run(null)} disabled={busy} className="rounded-full bg-primary text-primary-foreground" data-testid="accuracy-check-all">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Check entire question
          </Button>
          <Button variant="outline" onClick={() => run([...selected])} disabled={busy || selected.size === 0 || all} className="rounded-full" data-testid="accuracy-check-selected">
            Check {selected.size} selected
          </Button>
          <button type="button" onClick={() => setSelected(all ? new Set() : new Set(CHECK_FIELDS.map(([k]) => k)))} className="ml-auto font-mono text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground" data-testid="accuracy-toggle-all">{all ? "Clear all" : "Select all"}</button>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 sm:grid-cols-3" data-testid="accuracy-fields">
          {CHECK_FIELDS.map(([k, l]) => (
            <label key={k} className={`flex cursor-pointer items-center gap-2 text-xs ${draft?.[k] ? "text-foreground" : "text-muted-foreground/60"}`}>
              <Checkbox checked={selected.has(k)} onCheckedChange={() => toggle(k)} data-testid={`accuracy-field-${k}`} /> {l}
            </label>
          ))}
        </div>
        {busy && <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="accuracy-busy"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin text-gold" /> Fetching trusted translations, commentaries and lexicon for this verse, then reviewing your draft… A full-question check can take a minute or two.</div>}
        {result && <Results result={result} onApply={onApply} draft={draft} />}
      </div>
    </div>
  );
}

/* Apply a fix safely: targeted edits first; otherwise the full text, but never an excerpt (ellipsis / much shorter than the field). */
function applyFix(f, current, onApply) {
  const cur = String(current ?? "");
  let next = null;
  if (f.edits?.length && f.edits.every((e) => cur.includes(e.find))) {
    next = f.edits.reduce((t, e) => t.replace(e.find, e.replace ?? ""), cur);
  } else if (f.suggested_fix && !/^\s*(\.\.\.|…)/.test(f.suggested_fix) && (!cur || f.suggested_fix.length >= cur.length * 0.6)) {
    next = f.suggested_fix;
  }
  if (next === null) {
    toast.error("That suggestion is only an excerpt, so it wasn't applied — edit the field by hand using the note.");
    return;
  }
  onApply(f.field, next);
  toast.success(`Applied to ${f.label}.`, { action: { label: "Undo", onClick: () => onApply(f.field, cur) } });
}

function Results({ result, onApply, draft }) {
  const O = STATUS[result.overall] || STATUS.minor;
  return (
    <div className="space-y-3" data-testid="accuracy-results">
      <div className={`flex items-start gap-3 rounded-xl border p-3 ${O.cls}`} data-testid="accuracy-overall" data-status={result.overall}>
        <O.Icon className="mt-0.5 h-5 w-5 shrink-0" />
        <div><p className="font-semibold">Overall: {O.label}</p><p className="mt-0.5 text-sm">{result.summary}</p></div>
      </div>
      {result.fields.map((f) => {
        const S = STATUS[f.status] || STATUS.minor;
        return (
          <div key={f.field} className="rounded-xl border border-border p-3" data-testid={`accuracy-result-${f.field}`} data-status={f.status}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold text-foreground">{f.label}</span>
              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${S.cls}`}><S.Icon className="h-3 w-3" /> {S.label}</span>
            </div>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground/90">{f.notes}</p>
            {f.sources?.length > 0 && <p className="mt-1 font-mono text-[10px] text-muted-foreground">Checked against: {f.sources.join(" · ")}</p>}
            {(f.suggested_fix || f.edits?.length > 0) && (
              <div className="mt-2 rounded-lg bg-muted/60 p-2.5">
                <p className="font-mono text-[10px] uppercase tracking-wider text-gold">Suggested fix</p>
                {f.edits?.length > 0 ? f.edits.map((e, i) => (
                  <p key={`${e.find}-${i}`} className="mt-1 text-sm text-foreground"><span className="text-muted-foreground line-through decoration-red-400/70">{e.find}</span> <span className="text-gold">→</span> {e.replace}</p>
                )) : <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{f.suggested_fix}</p>}
                {onApply && f.field !== "related_scriptures" && (
                  <Button size="sm" variant="outline" className="mt-2 rounded-full" onClick={() => applyFix(f, draft?.[f.field], onApply)} data-testid={`accuracy-apply-${f.field}`}><Wand2 className="mr-1.5 h-3.5 w-3.5" /> {f.edits?.length ? "Apply edit" : "Apply to field"}</Button>
                )}
              </div>
            )}
          </div>
        );
      })}
      {result.suggestions?.length > 0 && (
        <div className="rounded-xl border border-dashed border-gold/50 bg-gold/5 p-3" data-testid="accuracy-suggestions">
          <p className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-gold"><Lightbulb className="h-3 w-3" /> Suggested additional sources — need your approval before use</p>
          <ul className="mt-1.5 space-y-1 text-sm text-foreground">{result.suggestions.map((s, i) => <li key={`${s.name}-${i}`}><span className="font-semibold">{s.name}</span> <span className="font-mono text-[10px] uppercase text-muted-foreground">{s.type}</span> — {s.why}</li>)}</ul>
          <p className="mt-1.5 text-[11px] text-muted-foreground">To allow one, add it to the trusted lists in “Trusted sources &amp; rules” above.</p>
        </div>
      )}
      <div className="rounded-xl border border-border p-3" data-testid="accuracy-evidence">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Evidence fetched for this check</p>
        <ul className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs">{result.evidence.map((e, i) => <li key={e.url || i}><a href={e.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">{e.label} <ExternalLink className="h-3 w-3" /></a></li>)}</ul>
        {result.evidence_notes?.length > 0 && <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">{result.evidence_notes.map((n, i) => <li key={`${i}-${n}`}>· {n}</li>)}</ul>}
      </div>
      <p className="font-mono text-[10px] text-muted-foreground">Model: {result.model} · Fields checked: {result.checked.length} · Evidence items: {result.evidence.length}</p>
    </div>
  );
}
